import { Trash2 } from 'lucide-react';
import { useState } from 'react';

import type { LocalRecord, Note, Task } from '../../shared/model';
import { isWorkspaceTrash } from '../../shared/trash';

import { workspace } from '../lib/engine';
import { ActionIconButton, Button, Modal, PageHeader } from './ui';

export function WorkspaceTrash({ records }: { records: LocalRecord[] }) {
  const items = records.filter(isWorkspaceTrash);

  const [deleting, setDeleting] = useState<string[] | null>(null),
    [busy, setBusy] = useState(false),
    [error, setError] = useState(''),
    [message, setMessage] = useState('');

  function confirm(ids: string[]) {
    setError('');
    setMessage('');
    setDeleting(ids);
  }

  async function remove() {
    if (!deleting) {
      return;
    }

    setBusy(true);
    setError('');

    try {
      await workspace.purgeWorkspace(deleting);
      setDeleting(null);

      setMessage(
        'Deleted on this device. Synchronization removes the items and their revision history from the server.',
      );
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <main className="productivity-page trash-page">
      <PageHeader
        title="Workspace trash"
        description="Restore notes and tasks, or permanently delete them when you no longer need them."
        actions={
          <ActionIconButton
            action="delete"
            label="Empty trash"
            disabled={!items.length || busy}
            onClick={() => confirm(items.map(r => r.id))}
          />
        }
      />
      {message && (
        <p className="notice" role="status">
          {message}
        </p>
      )}
      {error && !deleting && (
        <p className="error-message" role="alert">
          {error}
        </p>
      )}
      {!items.length && (
        <div className="empty-state">
          <Trash2 size={28} />
          <h2>Trash is empty.</h2>
          <p>Notes and tasks you move to trash will appear here.</p>
        </div>
      )}
      {items.map(r => (
        <div className="history-row" key={r.id}>
          <span>
            {(r.value as Note | Task).title}
            <small>{r.kind === 'task' ? 'Task' : 'Note'}</small>
          </span>
          <div className="button-row wrap">
            <ActionIconButton
              action="restore"
              label="Restore"
              disabled={busy}
              onClick={() =>
                void workspace
                  .save(r.id, r.kind, { ...r.value, trashed: false } as Note | Task)
                  .catch(e => setError((e as Error).message))
              }
            />
            <ActionIconButton
              action="delete"
              label="Delete permanently"
              disabled={busy}
              onClick={() => confirm([r.id])}
            />
          </div>
        </div>
      ))}
      {deleting && (
        <Modal
          open
          busy={busy}
          onClose={() => setDeleting(null)}
          title={`Permanently delete ${deleting.length} ${deleting.length === 1 ? 'item' : 'items'}?`}
        >
          <p>
            Their contents and live revision history will be removed. This cannot be undone.
            Previously earned goal credit and recorded focus time remain.
          </p>
          <p className="muted">
            Offline deletion completes when you sync. Existing backups and offline devices may
            retain copies until their retention period or next synchronization.
          </p>
          {error && (
            <p className="error-message" role="alert">
              {error}
            </p>
          )}
          <div className="button-row wrap">
            <Button variant="secondary" autoFocus disabled={busy} onClick={() => setDeleting(null)}>
              Cancel
            </Button>
            <Button variant="danger" disabled={busy} onClick={() => void remove()}>
              {busy ? 'Deleting…' : 'Permanently delete'}
            </Button>
          </div>
        </Modal>
      )}
    </main>
  );
}
